/**
 * Task/Issue status values.
 */
export type TaskStatus = 'Open' | 'Solved' | 'Closed';

/**
 * Valid TaskStatus values for runtime validation.
 */
export const VALID_TASK_STATUSES: readonly TaskStatus[] = ['Open', 'Solved', 'Closed'] as const;

/**
 * Validates if a value is a valid TaskStatus.
 */
export function isValidTaskStatus(value: unknown): value is TaskStatus {
  return typeof value === 'string' && VALID_TASK_STATUSES.includes(value as TaskStatus);
}

/**
 * Task/Issue type values.
 */
export type TaskType =
  'Problem' | 'Remark' | 'Task' | 'Appointment' | 'Document' | 'Contact' | 'Clash';

/**
 * Task/Issue priority values.
 */
export type TaskPriority = 'Low' | 'Medium' | 'High';

/**
 * Represents a user reference in a task (author, responsible, viewer, etc.)
 */
export interface TaskUser {
  /** User's unique identifier */
  id: string;
  /** User's email */
  email: string;
  /** User's display name */
  displayName: string;
  /** User's first name */
  firstName: string;
  /** User's last name */
  lastName: string;
}

/**
 * Scene/View template for a task.
 */
export interface TaskScene {
  /** Unique identifier (GUID) */
  id?: string;
  /** Parent object ID (task/issue ID) */
  parentId?: string;
  /** Order number for scene ordering */
  orderNumber?: number;
  /** Thumbnail as base64 data URL */
  thumbnailUrl: string;
  /** Camera state */
  camera?: {
    fov?: number;
    position?: [number, number, number];
    target?: [number, number, number];
    euler?: [number, number, number, string];
    isOrthographicCamera?: boolean;
  };
  /** Object visibility and selection state */
  objects?: {
    selected?: string[];
    highlighted?: string[];
    visible?: Record<string, boolean> | null;
  };
  /** Topology visibility per division */
  topology?: {
    id?: string;
    divisionTopologyId?: string;
    visible?: boolean;
    opaque?: boolean;
    opacity?: number;
  }[];
  /** Layer visibility per division */
  layers?: {
    id?: string;
    name?: string;
    divisionId?: string;
    divisionName?: string;
    divisionTopologyId?: string;
    visible?: boolean;
    opaque?: boolean;
    opacity?: number;
  }[];
  /** Attachments (e.g., thumbnail) */
  attachments?: {
    id?: string;
    url?: string;
    attachmentType?: string;
  }[];
}

/**
 * Domain model representing a task/issue within the application.
 */
export interface Task {
  /** Unique identifier (GUID) */
  id: string;

  /** Readable unique issue number */
  shortId: number;

  /** ID of the project (GUID) */
  projectId: string;

  /** Name of the task */
  name: string;

  /** Description of the task */
  description: string;

  /** Author of the task */
  author: TaskUser | null;

  /** Responsible person for the task */
  responsible: TaskUser | null;

  /** List of responsible people for the task */
  responsibles: TaskUser[];

  /** Status of the task */
  status: TaskStatus;

  /** Creation date of the task */
  createdAt: Date;

  /** Due date for the task */
  dueDate: Date | null;

  /** Type of the task */
  type: TaskType;

  /** The proposed solution for the task */
  solution: string;

  /** The classification to which the task belongs */
  classification: string;

  /** The priority of the task */
  priority: TaskPriority | null;

  /** Custom classification tag */
  tag: string;

  /** Modified date of the task */
  modifiedAt: Date | null;

  /** Scene/View of this task */
  scene: TaskScene | null;

  /** List of emails of users that will be additionally informed about changes */
  cc: string[];

  /** List of viewer people for the task */
  viewers: TaskUser[];

  /** ID of the clash (only if type = 'Clash') */
  clashId: string;

  /** Count of attachments attached to the task */
  attachmentsCount: number;

  /** Count of pins of the task */
  pinsCount: number;

  /** Count of comments to the task */
  commentsCount: number;

  /** Count of hyperlinks attached to the task */
  hyperlinksCount: number;

  /** Whether the task is published (false = draft) */
  published: boolean;
}

/**
 * Represents a user reference as returned from the API.
 */
export interface DtoTaskUser {
  id?: string;
  email?: string;
  displayname?: string;
  firstname?: string;
  lastname?: string;
}

/**
 * Scene/View template DTO as returned from the API (parsed from a JSON string).
 */
export interface DtoTaskScene {
  id?: string;
  name?: string;
  parentId?: string;
  /** Object ID (task/issue ID) that owns this scene */
  objectId?: string;
  createdDate?: string;
  modifiedDate?: string;
  orderNumber?: number;
  author?: { id?: string; email?: string | null };
  oldType?: boolean;
  overlays?: unknown[];
  /** Thumbnail as base64 data URL */
  thumbnail?: string;
  /** Thumbnail URL (returned when addThumbnailUrl=true is requested) */
  thumbnailUrl?: string;
  viewMode?: string;
  viewBox?: {
    position?: [number, number, number];
    size?: [number, number, number];
  };
  camera?: {
    fov?: number;
    position?: [number, number, number];
    target?: [number, number, number];
    euler?: [number, number, number, string];
    isOrthographicCamera?: boolean;
    AspectRatio?: number | null;
    /** Scale factor for view to world coordinates */
    viewToWorldScale?: number | null;
  };
  objects?: {
    selected?: string[];
    highlighted?: string[];
    transparent?: string[];
    highlightedSelected?: string[];
    coloring?: Record<string, unknown>;
    visible?: Record<string, boolean> | null;
    /** Additional info about selected objects */
    selectedInfo?: unknown[];
  };
  /** Single clipping plane (legacy) */
  clippingPlane?: unknown;
  clippingPlanes?: unknown[];
  rotationCenter?: [number, number, number];
  topology?: {
    id?: string;
    divisionTopologyId?: string;
    visible?: boolean;
    opaque?: boolean;
    opacity?: number;
    /** Revision number for this topology entry */
    revision?: number;
  }[];
  layers?: {
    revision?: number;
    id?: string;
    name?: string;
    divisionId?: string;
    divisionName?: string;
    divisionTopologyId?: string;
    revisions?: number[];
    visible?: boolean;
    opaque?: boolean;
    opacity?: number;
    valid?: boolean;
  }[];
  attachments?: {
    id?: string;
    url?: string;
    attachmentType?: string;
  }[];
  version?: number[];
}

/**
 * Document transfer object representing the task data as received from the backend API.
 * Note: The API uses "issues" naming, but the domain uses "tasks".
 */
export interface DtoTask {
  /** Unique identifier (GUID) */
  id?: string;

  /** Readable unique issue number (always starts with 1 for each project) */
  shortId?: number;

  /** ID of the project (GUID) */
  projectId?: string;

  /** Name of the task */
  name?: string;

  /** Description of the task */
  description?: string;

  /** Author of the task */
  author?: DtoTaskUser;

  /** Responsible person for the task */
  responsible?: DtoTaskUser;

  /** List of responsible people for the task - must be members of project */
  responsibles?: DtoTaskUser[];

  /** Status of the task (default: 'Open') */
  status?: TaskStatus;

  /** Creation date of the task (ISO date string) */
  createdAt?: string;

  /** The date in which the task is planned to be finished (ISO date string) */
  dueDate?: string;

  /** Type of the task (default: 'Problem') */
  type?: TaskType;

  /** The proposed solution for the task */
  solution?: string;

  /** The classification to which the task belongs */
  classification?: string;

  /** The priority of the task */
  priority?: TaskPriority;

  /** Custom classification tag */
  tag?: string;

  /** Modified date of the task (ISO date string) */
  modifiedAt?: string;

  /** Scene/View of this task — the API returns this as a stringified JSON string,
   * or as a parsed object when addThumbnailUrl=true is requested */
  scene?: string | DtoTaskScene;

  /** Array of scene IDs attached to this task (parallel scenes list) */
  scenesId?: string[];

  /** List of emails of users that will be additionally informed about changes */
  cc?: string[];

  /** List of viewer people for the task - must be members of project */
  viewers?: DtoTaskUser[];

  /** ID of the clash (only if type = 'Clash') */
  clashId?: string;

  /** Count of attachments attached to the task */
  attachmentsCount?: number;

  /** Count of pins of the task */
  pinsCount?: number;

  /** Count of comments to the task */
  commentsCount?: number;

  /** Count of hyperlinks attached to the task */
  hyperlinksCount?: number;

  /** Whether the task is published (false = draft) */
  published?: boolean;
}

/**
 * Parameters for fetching tasks/issues.
 */
export interface GetTasksParams {
  /** If true, scene will not be loaded, only task data will be returned */
  filterScene?: boolean;

  /** If true, the scene object will be returned containing only thumbnailUrl */
  addThumbnailUrl?: boolean;
}
